/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'
import { Captions } from 'lucide-react'
import { ClientOnly } from '../SSRSwitch'
import { MyListButton } from '..'
import styles from './styles'
import { API_IMAGE_OBJECT } from '../../utils/types'
import ForceAdsWarning from '../AdLabels/ForceAdsWarning'
import HeroLiveIcon from '../icons/HeroLiveIcon'
import HeroPlayIcon from '../icons/HeroPlayIcon'
import SvodExclusiveWarning from '../AdLabels/SvodExclusiveWarning'
import PadlockIcon from '../icons/PadlockIcon'
import EllipseIcon from '../icons/EllipseIcon'
import GuideIcon from '../icons/GuideIcon'
import AudioDescibedIcon from '../icons/AudioDescibedIcon'
import VisuallySignedIcon from '../icons/VisuallySignedIcon'
import { InformationIcon } from '../icons'
import config from './config'
import { homepageClick } from '../../utils/dtmHelpers'
import { sendGoogleTagEvent } from '../../utils/google-tag-manager-helpers'

const HeroCTA = ({
  title,
  text,
  buttonText,
  buttonLink,
  buttonType,
  forceAds,
  svodExclusive,
  genre,
  availableEpisodes,
  isGuidance,
  audioDescribed,
  visuallySigned,
  isSubtitle,
  programmeHead,
  programmeDetails,
  hasInformation,
  informationText,
  competitionText,
  hasCompetitionText,
  svodProhibited,
  vipExclusive,
  vipProhibited,
  isHomepage,
  isStvLiveProg,
  index,
  liveProgramme,
  isLive,
}) => {
  let buttonIcon = null
  const [limitedText, setLimitedText] = useState(false)
  const displayButton = buttonType !== 'none'
  let exclusiveButton = {}
  let finalButtonLink = liveProgramme ? '/live/stv' : buttonLink
  const users = useSelector(stateSelector => stateSelector.user)

  useEffect(() => {
    if (window.innerWidth >= 768) {
      setLimitedText(false)
    } else if (text !== null && !programmeHead) {
      /* eslint-disable no-unused-expressions */
      text.length >= 130
        ? setLimitedText(true) : setLimitedText(false)
    }
  }, [text])

  if (buttonType === 'watchNow') {
    buttonIcon = <HeroPlayIcon />
  } else if (buttonType === 'watchLive' || liveProgramme) {
    buttonIcon = <HeroLiveIcon />
  }

  function capitalizeFirstLetter(string) {
    let splitedString
    if (!string) { return null }
    if (string) {
      splitedString = string.split(/(?=[A-Z])/).join(' ')
      splitedString = splitedString.charAt(0).toUpperCase() + splitedString.slice(1)
    }
    return splitedString
  }

  let finalButtonText = liveProgramme ? 'Watch Live' : ((buttonType === 'custom' || programmeHead) ? buttonText : capitalizeFirstLetter(buttonType))

  if (svodExclusive && !users.currentlySubscribed) {
    buttonIcon = <PadlockIcon />
    finalButtonText = ' Upgrade to STV Player+'
    exclusiveButton = { background: '#F6F6F6', color: '#151515' }
    finalButtonLink = 'https://my.stv.tv/plan'
  }

  const analyticsTracking = (event) => {
    const tilePosition = index + 1 // index is 0 based
    homepageClick('Hero Image', 0, tilePosition, event.target.title)
    // datalayer push here
    sendGoogleTagEvent('Watch Now Button', {
      item: event.target.title,
    })
  }

  const hasIcons = isGuidance
    || audioDescribed
    || visuallySigned
    || isSubtitle
    || hasInformation
    || hasCompetitionText
    || svodExclusive
    || forceAds

  const showForceAdsIcon = programmeHead && forceAds && users.currentlySubscribed
  const showSvodExclusiveIcon = svodExclusive && !users.currentlySubscribed

  return (
    <div className={`HeroCTA ${isHomepage ? 'homepage' : null}`}>
      <h1 className="hero-cta-title">{title}</h1>
      {buttonType === 'watchLive' && !isStvLiveProg ? (
        // Simple genre-only view for live content
        genre && (
          <div className="progmDetails">
            <p className="genre">{genre}</p>
          </div>
        )
      ) : (
        // Full details view for other content types
        (genre || availableEpisodes || hasIcons) && (
          <div className="progmDetails">
            {genre && <p className="genre">{genre}</p>}
            {genre && availableEpisodes && <div className="iconWrap"><EllipseIcon /></div>}
            {availableEpisodes && <p className="genre">{availableEpisodes}</p>}
            {availableEpisodes && hasIcons && <div className="iconWrap"><EllipseIcon /></div>}
            {hasIcons && (
              <div className="hero-icons">
                {isSubtitle && (
                  <div data-tooltip-id="subtitle">  <Captions />
                    <Tooltip id="subtitle" place="bottom" effect="solid" style={{ backgroundColor: '#313131', borderRadius: '8px' }}>
                      {config.subtitle}
                    </Tooltip>
                  </div>
                )}
                {isGuidance && (
                  <div data-tooltip-id="guidance"> <GuideIcon />
                    <Tooltip id="guidance" place="bottom" effect="solid" style={{ backgroundColor: '#313131', borderRadius: '8px' }}>
                      {config.guidance}
                    </Tooltip>
                  </div>
                )}
                {audioDescribed && (
                  <div data-tooltip-id="audioDesc"> <AudioDescibedIcon />
                    <Tooltip id="audioDesc" place="bottom" effect="solid" style={{ backgroundColor: '#313131', borderRadius: '8px' }}>
                      {config.audioDesc}
                    </Tooltip>
                  </div>
                )}
                {visuallySigned && (
                  <div data-tooltip-id="signed"> <VisuallySignedIcon />
                    <Tooltip id="signed" place="bottom" effect="solid" style={{ backgroundColor: '#313131', borderRadius: '8px' }}>
                      {config.signed}
                    </Tooltip>
                  </div>
                )}
                {hasInformation && (
                  <div data-tooltip-id="infoText"> <InformationIcon />
                    <Tooltip id="infoText" place="bottom" effect="solid" style={{ backgroundColor: '#313131', borderRadius: '8px', width: '300px' }}>
                      {informationText}
                    </Tooltip>
                  </div>
                )}
                {hasCompetitionText && (
                  <div data-tooltip-id="CompetitionText"> <InformationIcon />
                    <Tooltip id="CompetitionText" place="bottom" effect="solid">
                      {competitionText}
                    </Tooltip>
                  </div>
                )}
                {programmeHead && (showForceAdsIcon || showSvodExclusiveIcon) && (
                  <div className="iconWrap"><EllipseIcon /></div>
                )}
                {svodExclusive && !users.currentlySubscribed && (
                  <div data-tooltip-id="svodExclusive"> <SvodExclusiveWarning />
                    <Tooltip id="svodExclusive" place="bottom" effect="solid">
                      {config.svodExculsive}
                    </Tooltip>
                  </div>
                )}
                {programmeHead && forceAds && (
                  <div data-tooltip-id="forceAds"> <ForceAdsWarning />
                    <Tooltip id="forceAds" place="bottom" effect="solid">
                      {config.forceAds}
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      )}
      <p className="hero-cta-text">{limitedText && text !== null ? `${text.substring(0, 130)}...` : text}</p>

      {svodExclusive && !programmeHead && (
        <div className="svod-exclusive-warning">
          <SvodExclusiveWarning includeText />
        </div>
      )}

      {forceAds && programmeHead && (
        <div className="force-ads-warning">
          <ForceAdsWarning includeText />
        </div>
      )}

      {displayButton && !programmeHead && (
        <div className="hero-button-link">
          <a href={finalButtonLink} title={title ?? 'No Title'} onClick={analyticsTracking}>
            <button type="button" title={title ?? 'No Title'} className={`hero-button ${((buttonType === 'watchLive') || liveProgramme) ? 'live' : ''}`} style={exclusiveButton}>
              { buttonIcon }
              { finalButtonText }
            </button>
          </a>
        </div>
      )}

      {programmeHead && (
        <div className="button-wrapper">
          {/** No onClick handler here as we don't need programme hero tracking */}
          <a href={finalButtonLink} title={title ?? 'No Title'}>
            <button type="button" title={title ?? 'No Title'} className={`hero-button-program ${isStvLiveProg ? 'live' : ''}`} style={exclusiveButton}>
              { buttonIcon }
              {finalButtonText}
            </button>
          </a>
          <div className="wrapmylist">
            <ClientOnly>
              <MyListButton
                programme={{
                  name: programmeDetails.title,
                  image: programmeDetails.images,
                  programmeGuid: programmeDetails.guid,
                }}
                programmeHead
                addedStyles={{
                  minWidth: `auto`,
                  height: '42px',
                  background: 'none',
                  fontWeight: '400',
                  fontSize: '18px',
                  justifyContent: 'start',
                  gap: '8px',
                  padding: '0px',
                  alignItems: 'center',
                  color: '#F6F6F6',
                }}
                color="#F6F6F6"
                addClass="Program-page-myList"
              />
            </ClientOnly>
          </div>
        </div>
      )}
      <style jsx>{styles}</style>
    </div>

  )
}

HeroCTA.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonType: PropTypes.string,
  forceAds: PropTypes.bool,
  svodExclusive: PropTypes.bool,
  svodProhibited: PropTypes.bool,
  vipExclusive: PropTypes.bool,
  vipProhibited: PropTypes.bool,
  genre: PropTypes.string,
  availableEpisodes: PropTypes.string,
  isGuidance: PropTypes.bool,
  audioDescribed: PropTypes.bool,
  visuallySigned: PropTypes.bool,
  isSubtitle: PropTypes.bool,
  programmeHead: PropTypes.bool,
  hasInformation: PropTypes.bool,
  hasCompetitionText: PropTypes.bool,
  informationText: PropTypes.string,
  competitionText: PropTypes.string,
  programmeDetails: PropTypes.shape({
    title: PropTypes.string,
    images: API_IMAGE_OBJECT,
    guid: PropTypes.string,
  }),
  isHomepage: PropTypes.bool,
  index: PropTypes.number,
  liveProgramme: PropTypes.bool,
  isStvLiveProg: PropTypes.bool,
  isLive: PropTypes.bool,
}

HeroCTA.defaultProps = {
  title: '',
  text: '',
  buttonText: 'Watch live',
  buttonLink: '',
  buttonType: 'watchNow',
  forceAds: false,
  svodExclusive: false,
  svodProhibited: false,
  vipExclusive: false,
  vipProhibited: false,
  genre: '',
  availableEpisodes: '',
  isGuidance: false,
  audioDescribed: false,
  visuallySigned: false,
  isSubtitle: false,
  programmeHead: false,
  programmeDetails: {},
  hasInformation: false,
  competitionText: '',
  informationText: '',
  hasCompetitionText: false,
  isHomepage: false,
  index: 0,
  liveProgramme: false,
  isStvLiveProg: false,
  isLive: false,
}
export default HeroCTA
